import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { ConfettiCelebration } from '../../accounts/components/ConfettiCelebration';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { soundManager } from '../../utils/soundManager';
import { HUNDRED_CORRECT_REWARD, type VideoRewardId } from '../../utils/videoRewardManager';
/**
 * VideoRewardModal — F4.8 (APP_FEEL_2027_UPGRADE §11.1)
 *
 * Fullscreen celebration for a one-shot symbolic milestone. Per §12.C the
 * original video plan was reduced to an animated SVG celebration to avoid:
 *   - school-WiFi blocking of MP4 CDNs
 *   - browser autoplay restrictions on Chromebooks
 *   - bandwidth costs on slow networks
 *   - video maintenance burden on rebrand
 *
 * Always closable via the skip button or Escape key.
 */
interface RewardCopy {
  headline: string;
  djayQuote: string;
  ctaLabel: string;
}
const REWARD_COPY: Record<VideoRewardId, RewardCopy> = {
  [HUNDRED_CORRECT_REWARD]: {
    headline: 'Honderd!',
    djayQuote: 'Honderd vragen goed?! Wow, dat is écht honderd! Ik ben super trots op je. Blijf zo doorgaan!',
    ctaLabel: 'Lekker bezig!'
  }
};
interface VideoRewardModalProps {
  rewardId: VideoRewardId;
  isOpen: boolean;
  onClose: () => void;
}
export function VideoRewardModal({
  rewardId,
  isOpen,
  onClose
}: VideoRewardModalProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const focusRef = useFocusTrap<HTMLDivElement>(isOpen);
  useEscapeKey(onClose, isOpen);
  const copy = REWARD_COPY[rewardId];
  // Celebratory chord on open — non-fatal if sound is muted.
  useEffect(() => {
    if (!isOpen) return;
    try {
      soundManager.playGoalReached();
    } catch {
      /* silent */
    }
  }, [isOpen]);
  return <AnimatePresence data-id="element-2427">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} role="dialog" aria-modal="true" aria-labelledby="video-reward-headline" className="fixed inset-0 z-[200] flex items-center justify-center" style={{
      background: 'radial-gradient(ellipse at center, rgba(59,130,246,0.85) 0%, rgba(30,41,59,0.95) 70%, rgba(15,23,42,0.98) 100%)'
    }} data-id="element-2428">
          {/* Confetti behind everything */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-2429">
            <ConfettiCelebration data-id="element-2430" />
          </div>

          {/* Skip / close button — always reachable */}
          <button type="button" onClick={onClose} aria-label="Sluit verrassing" className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white/60" data-id="element-2431">
            <X className="w-5 h-5" aria-hidden data-id="element-2432" />
          </button>

          {/* Reward card */}
          <motion.div ref={focusRef} initial={{
        scale: 0.6,
        opacity: 0,
        y: 40
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.8,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 220,
        damping: 22,
        delay: 0.1
      }} className="relative w-[min(92vw,480px)] mx-4 rounded-3xl bg-white/95 backdrop-blur-xl px-6 sm:px-8 py-8 sm:py-10 shadow-2xl text-center" data-id="element-2433">
            {/* Sparkle ring around Djay */}
            <div className="relative mx-auto w-32 h-32 sm:w-40 sm:h-40 mb-4" data-id="element-2434">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute inset-0 flex items-center justify-center" aria-hidden data-id="element-2435">
                {[0, 60, 120, 180, 240, 300].map(deg => <Sparkles key={deg} className="absolute w-4 h-4 text-amber-400" style={{
              transform: `rotate(${deg}deg) translateY(-72px)`
            }} data-id="element-2436" />)}
              </motion.div>
              <motion.div animate={{
            scale: [1, 1.05, 1]
          }} transition={{
            duration: 1.4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute inset-4 flex items-center justify-center" data-id="element-2437">
                <DjayAvatar emotion="celebrating" variant={selectedVariant} size="xl" data-id="element-2438" />
              </motion.div>
            </div>

            {/* Headline — the symbolic number */}
            <motion.h2 id="video-reward-headline" initial={{
          scale: 0.5,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 18,
          delay: 0.3
        }} className="text-5xl sm:text-6xl font-black tracking-tight bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 bg-clip-text text-transparent mb-3" data-id="element-2439">
              {copy.headline}
            </motion.h2>

            {/* Djay quote */}
            <motion.p initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.35
        }} className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 max-w-[36ch] mx-auto" data-id="element-2440">
              {copy.djayQuote}
            </motion.p>

            {/* CTA */}
            <motion.button type="button" onClick={onClose} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.3
        }} whileTap={{
          scale: 0.96
        }} className="px-8 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-base shadow-lg shadow-amber-500/30 focus:outline-none focus:ring-4 focus:ring-amber-300/60 transition-all" data-id="element-2441">
              {copy.ctaLabel}
            </motion.button>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}